/**
 * Дзеркало backend `apps/api/src/modules/persons/filter-tree.ts`. Тримаємо
 * sync вручну: коли додаєш нове поле / op на бекенді — оновлюй тут теж.
 *
 * Тип поля — це FieldTypeKey з catalog'у FIELD_TYPES, який також
 * використовується у "Налаштувати поля" (DataTablesManager) і у picker'і
 * "Додати поле". Тобто та сама єдина база — одне поле з типом `email`
 * виглядає однаково всюди (іконка ✉️, лейбл "Email", той самий набір
 * операторів у фільтрі).
 */

import { FIELD_TYPES, type FieldTypeKey } from './data-tables-catalog';

export type Operator =
  | 'eq' | 'neq' | 'contains' | 'not_contains'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'between' | 'before' | 'after'
  | 'has_value' | 'has_no_value'
  | 'is_true' | 'is_false';

export interface Condition {
  _id?: string;
  field: string;
  op: Operator;
  value?: unknown;
}

export interface Group {
  _id?: string;
  combinator: 'AND' | 'OR';
  conditions: (Condition | Group)[];
}

let _idSeq = 0;
function freshId(): string {
  _idSeq += 1;
  return `n${_idSeq}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Тип поля для фільтра — той самий FieldTypeKey що в каталозі. */
export type FieldType = FieldTypeKey;

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  enumOptions?: { value: string; label: string }[];
}

export const PERSON_FIELDS: FieldDef[] = [
  { key: 'displayName', label: "Ім'я", type: 'single_line' },
  { key: 'username', label: 'Telegram username', type: 'single_line' },
  { key: 'currentTariff', label: 'Тариф', type: 'single_line' },
  {
    key: 'currentSubscriptionStatus',
    label: 'Статус підписки',
    type: 'dropdown',
    enumOptions: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'none', label: 'No subscription' },
    ],
  },
  { key: 'ltvUah', label: 'LTV, ₴', type: 'currency' },
  { key: 'paymentCount', label: 'Кількість платежів', type: 'number' },
  { key: 'firstSeenAt', label: 'First seen', type: 'date' },
  { key: 'lastSeenAt', label: 'Last seen', type: 'date' },
  { key: 'subscriptionEndsAt', label: 'Підписка закінчується', type: 'date' },
  { key: 'identity_email', label: 'Email', type: 'email' },
  { key: 'identity_phone', label: 'Телефон', type: 'phone' },
  { key: 'identity_telegram', label: 'Telegram ID', type: 'single_line' },
  { key: 'identity_sp', label: 'SendPulse contact ID', type: 'single_line' },
];

export const PAYMENT_FIELDS: FieldDef[] = [
  {
    key: 'status',
    label: 'Статус',
    type: 'dropdown',
    enumOptions: [
      { value: 'SUCCEEDED', label: 'SUCCEEDED' },
      { value: 'REFUNDED', label: 'REFUNDED' },
      { value: 'FAILED', label: 'FAILED' },
      { value: 'PENDING', label: 'PENDING' },
      { value: 'CHARGEBACK', label: 'CHARGEBACK' },
    ],
  },
  { key: 'rawStatus', label: 'Raw status', type: 'single_line' },
  {
    key: 'provider',
    label: 'Acquirer',
    type: 'dropdown',
    enumOptions: [
      { value: 'WAYFORPAY', label: 'WayForPay' },
      { value: 'LIQPAY', label: 'LiqPay' },
      { value: 'FONDY', label: 'Fondy' },
      { value: 'PORTMONE', label: 'Portmone' },
      { value: 'STRIPE', label: 'Stripe' },
      { value: 'UNKNOWN', label: 'Unknown' },
    ],
  },
  { key: 'amountUah', label: 'Сума, ₴', type: 'currency' },
  { key: 'feeUah', label: 'Комісія, ₴', type: 'currency' },
  { key: 'netUah', label: 'Net, ₴', type: 'currency' },
  { key: 'refundAmountUah', label: 'Сума refund, ₴', type: 'currency' },
  { key: 'currency', label: 'Валюта', type: 'single_line' },
  { key: 'externalRef', label: 'Order ref', type: 'single_line' },
  { key: 'processorPaymentId', label: 'Processor payment ID', type: 'single_line' },
  { key: 'merchantName', label: 'Merchant name', type: 'single_line' },
  { key: 'merchantId', label: 'Merchant ID', type: 'single_line' },
  { key: 'paymentMethod', label: 'Метод оплати', type: 'single_line' },
  { key: 'cardBrand', label: 'Бренд картки', type: 'single_line' },
  { key: 'cardLast4', label: 'Card last 4', type: 'single_line' },
  { key: 'cardBin', label: 'Card BIN', type: 'single_line' },
  { key: 'rawTariff', label: 'Raw tariff', type: 'single_line' },
  { key: 'productSku', label: 'Product SKU', type: 'single_line' },
  { key: 'payerEmail', label: 'Email платника', type: 'email' },
  { key: 'payerPhone', label: 'Phone платника', type: 'phone' },
  { key: 'payerFirstName', label: "Ім'я платника", type: 'single_line' },
  { key: 'payerLastName', label: 'Прізвище платника', type: 'single_line' },
  { key: 'payerCountry', label: 'Країна платника', type: 'country' },
  { key: 'occurredAt', label: 'Час платежу', type: 'date' },
  { key: 'requestedAt', label: 'Час запиту', type: 'date' },
  { key: 'refundedAt', label: 'Час refund', type: 'date' },
  { key: 'chargebackAt', label: 'Час chargeback', type: 'date' },
  { key: 'importedAt', label: 'Час імпорту', type: 'date' },
  { key: 'inferred', label: 'Inferred', type: 'checkbox' },
  { key: 'errorCode', label: 'Error code', type: 'single_line' },
  { key: 'errorMessage', label: 'Error message', type: 'multi_line' },
  { key: 'refundReason', label: 'Refund reason', type: 'multi_line' },
  { key: 'importedFrom', label: 'Imported from', type: 'single_line' },
];

/** Field registry за scope'ом. UI використовує цю функцію щоб вибрати поля. */
export function fieldsForScope(scope: string): FieldDef[] {
  if (scope === 'payments') return PAYMENT_FIELDS;
  return PERSON_FIELDS;
}

/**
 * Helper: ApiField (з catalog'у custom-полів) → FieldDef для filter-builder.
 * Backend filter-tree розпарсить key='customFields.<fieldKey>' як jsonb-path.
 */
export function apiFieldToFilterDef(f: {
  fieldKey: string;
  label: string;
  type: FieldType;
}): FieldDef {
  return {
    key: `customFields.${f.fieldKey}`,
    label: f.label,
    type: f.type,
  };
}

// =============================================================================
//  Operators per FieldType.
//  Всі catalog-типи мапляться в одну з декількох "категорій операторів":
//    text-like:   contains/not_contains/eq/neq/has_value/has_no_value
//    numeric:     eq/neq/gt/gte/lt/lte/between/has_value/has_no_value
//    date:        between/before/after/eq/has_value/has_no_value
//    enum:        eq/neq/has_value/has_no_value
//    boolean:     is_true/is_false
//    has-only:    has_value/has_no_value (для polymorphic типів типу formula)
// =============================================================================

const TEXT_LIKE: FieldTypeKey[] = [
  'single_line', 'multi_line', 'email', 'phone', 'url',
  'country', 'tags',
];
const NUMERIC: FieldTypeKey[] = ['number', 'auto_number', 'percent', 'currency', 'stat'];
const HAS_ONLY: FieldTypeKey[] = ['formula', 'linked_record', 'linked_summary'];

const UNIVERSAL: { value: Operator; label: string }[] = [
  { value: 'has_value', label: 'має будь-яке значення' },
  { value: 'has_no_value', label: 'не має жодного значення' },
];

export function operatorsFor(type: FieldType): { value: Operator; label: string }[] {
  if (TEXT_LIKE.includes(type)) {
    return [
      { value: 'contains', label: 'містить' },
      { value: 'not_contains', label: 'не містить' },
      { value: 'eq', label: 'дорівнює' },
      { value: 'neq', label: 'не дорівнює' },
      ...UNIVERSAL,
    ];
  }
  if (NUMERIC.includes(type)) {
    return [
      { value: 'eq', label: 'є' },
      { value: 'neq', label: 'не є' },
      { value: 'gt', label: '>' },
      { value: 'gte', label: '≥' },
      { value: 'lt', label: '<' },
      { value: 'lte', label: '≤' },
      { value: 'between', label: 'у діапазоні' },
      ...UNIVERSAL,
    ];
  }
  if (type === 'date') {
    return [
      { value: 'between', label: 'у діапазоні' },
      { value: 'before', label: 'до' },
      { value: 'after', label: 'після' },
      { value: 'eq', label: 'у день' },
      ...UNIVERSAL,
    ];
  }
  if (type === 'dropdown') {
    return [
      { value: 'eq', label: 'є' },
      { value: 'neq', label: 'не є' },
      ...UNIVERSAL,
    ];
  }
  if (type === 'checkbox') {
    return [
      { value: 'is_true', label: 'так' },
      { value: 'is_false', label: 'ні' },
    ];
  }
  if (HAS_ONLY.includes(type)) {
    return [...UNIVERSAL];
  }
  // Fallback (default) — лишаємо text-ops щоб не блокувати unknown типи.
  return [
    { value: 'eq', label: 'дорівнює' },
    { value: 'contains', label: 'містить' },
    ...UNIVERSAL,
  ];
}

// Гарантуємо що всі FIELD_TYPES відомі operatorsFor — silent guard.
void FIELD_TYPES;

export function emptyGroup(): Group {
  return { _id: freshId(), combinator: 'AND', conditions: [] };
}
export function emptyCondition(fieldKey?: string): Condition {
  return { _id: freshId(), field: fieldKey ?? PERSON_FIELDS[0]!.key, op: 'contains', value: '' };
}
export function isGroup(x: Condition | Group): x is Group {
  return typeof x === 'object' && x !== null && 'combinator' in x && 'conditions' in x;
}

export function reassignIds(node: Group | Condition): void {
  node._id = freshId();
  if (isGroup(node)) {
    for (const c of node.conditions) reassignIds(c);
  }
}

export function countActive(g: Group): number {
  let n = 0;
  for (const c of g.conditions) {
    if (isGroup(c)) {
      n += countActive(c);
    } else {
      const noValueOps: Operator[] = ['has_value', 'has_no_value', 'is_true', 'is_false'];
      if (noValueOps.includes(c.op)) {
        n++;
      } else if (c.value !== undefined && c.value !== null && c.value !== '') {
        if (Array.isArray(c.value)) {
          if (c.value.some((v) => v !== '' && v !== null && v !== undefined)) n++;
        } else {
          n++;
        }
      }
    }
  }
  return n;
}

export function isEmpty(g: Group): boolean {
  return countActive(g) === 0;
}
